/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect } from "react";
import { Box, MenuItem, TextField, Typography, useTheme } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { tokens } from "../../../theme";
import { UserContext } from "../../../contexts";
import MultiSelect from "../../../components/MultiSelect";
import { toCamelCase } from "../../../utils/helperFunctions";
import {
  CONTROL_PLANE_SERVICE_PLAN,
  CONTROL_PLANE_CLOUD_TYPE,
  GLOBAL_ENVIRONMENT_NAME,
  DATA_PLANE_CLOUD_TYPE,
} from "../../../utils/constants";
import { useGetLabelsToCreate } from "../../../api/envs";
import { requestCPDialogStyles } from "../styles";
import { getJiraIdError } from "../utils";

const FieldLabel = ({ colors, label, required }) => (
  <Typography component="label" sx={requestCPDialogStyles.fieldLabel(colors)}>
    {label}
    {required ? (
      <Box component="span" sx={requestCPDialogStyles.requiredMark(colors)}>
        {" *"}
      </Box>
    ) : null}
  </Typography>
);

const RequestCPDialogContent = ({ requestCPPayload, setRequestCPPayload }) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const loggedInUser = useContext(UserContext);
  useEffect(() => {
    setRequestCPPayload({
      approverId: loggedInUser?.data?.quotas?.controlPlane?.allocated
        ? loggedInUser?.data?.id
        : loggedInUser?.data?.manager?.id,
      projectName: "",
      releaseLabel: "",
      justification: "",
      globalEnvironmentName: "",
      jiraId: "",
      servicePlan: CONTROL_PLANE_SERVICE_PLAN,
      cloudType: "",
    });
  }, []);

  const { postData: getLabels, response: getLabelsResp } =
    useGetLabelsToCreate();

  useEffect(() => {
    getLabels();
  }, []);

  // Labels are rendered above each field, so the inputs themselves carry no
  // floating MUI label; the label text still derives the payload key.
  const textFieldCommonProps = (label) => ({
    fullWidth: true,
    size: "small",
    color: "secondary",
    value: requestCPPayload?.[toCamelCase(label)] ?? "",
    onChange: (e) => {
      setRequestCPPayload((prev) => ({
        ...prev,
        [toCamelCase(label)]: e.target.value,
      }));
    },
  });

  const jiraIdError = getJiraIdError(requestCPPayload?.jiraId);

  return (
    <Box sx={requestCPDialogStyles.form}>
      <Box sx={requestCPDialogStyles.infoBanner(colors)}>
        <InfoOutlinedIcon sx={requestCPDialogStyles.infoIcon(colors)} />
        <Typography sx={requestCPDialogStyles.infoText(colors)}>
          Your request goes to your manager for approval. You will be notified
          once the control plane is provisioned.
        </Typography>
      </Box>

      <Box>
        <FieldLabel colors={colors} label="Project Name" required />
        <TextField
          {...textFieldCommonProps("Project Name")}
          onChange={(e) => {
            /^[a-z0-9]*$/.test(e.target.value) &&
              setRequestCPPayload((prev) => ({
                ...prev,
                projectName: e.target.value,
              }));
          }}
          helperText="allowed chars: [a-z0-9]"
          FormHelperTextProps={{ sx: requestCPDialogStyles.helperText(colors) }}
        />
      </Box>

      <Box sx={requestCPDialogStyles.fieldRow}>
        <Box>
          <FieldLabel colors={colors} label="Release Label" required />
          <TextField {...textFieldCommonProps("Release Label")} select>
            {getLabelsResp?.response?.map((label, i) => (
              <MenuItem value={label} key={i}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box>
          <FieldLabel colors={colors} label="Global Environment" required />
          <TextField
            {...textFieldCommonProps("Global Environment Name")}
            select
          >
            {GLOBAL_ENVIRONMENT_NAME.map((globalEnv, i) => (
              <MenuItem value={globalEnv} key={i}>
                {globalEnv}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      <Box>
        <FieldLabel colors={colors} label="Justification" required />
        <TextField
          {...textFieldCommonProps("Justification")}
          multiline
          minRows={3}
          maxRows={5}
        />
      </Box>

      <Box sx={requestCPDialogStyles.fieldRow}>
        <Box>
          <FieldLabel colors={colors} label="Manager" />
          <TextField
            {...textFieldCommonProps("Manager")}
            value={loggedInUser?.data?.manager?.email ?? ""}
            disabled
          />
        </Box>
        <Box>
          <FieldLabel colors={colors} label="Jira Id" required />
          <TextField
            {...textFieldCommonProps("Jira Id")}
            error={Boolean(jiraIdError)}
            helperText={jiraIdError || "Feature/Epic/Bug, e.g. TS-1234"}
            FormHelperTextProps={{
              sx: jiraIdError
                ? requestCPDialogStyles.errorText(colors)
                : requestCPDialogStyles.helperText(colors),
            }}
          />
        </Box>
      </Box>

      <Box sx={requestCPDialogStyles.section(colors)}>
        <Typography sx={requestCPDialogStyles.sectionTitle(colors)}>
          Cloud Configuration
        </Typography>
        <Box sx={requestCPDialogStyles.fieldRow}>
          <Box>
            <FieldLabel colors={colors} label="Service Plan" />
            <TextField
              value={CONTROL_PLANE_SERVICE_PLAN}
              fullWidth
              size="small"
              disabled
              color="secondary"
            />
          </Box>
          <Box>
            <FieldLabel colors={colors} label="Control Plane Cloud" required />
            <TextField {...textFieldCommonProps("Cloud Type")} select>
              {CONTROL_PLANE_CLOUD_TYPE.map((cloud, i) => (
                <MenuItem value={cloud} key={i}>
                  {cloud}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>
        <Box>
          <FieldLabel colors={colors} label="Data Plane Clouds" />
          <MultiSelect
            setSelectedItems={(newVal) => {
              setRequestCPPayload((prev) => ({
                ...prev,
                dataPlaneClouds: newVal,
              }));
            }}
            limit={1}
            sx={requestCPDialogStyles.multiSelect}
            chipSx={requestCPDialogStyles.multiSelectChip(colors)}
            textFieldProps={{ size: "small", color: "secondary" }}
            populateItemsWhenEmpty={false}
            allItems={DATA_PLANE_CLOUD_TYPE}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default RequestCPDialogContent;
